import { styled, alpha, keyframes } from "@mui/material/styles";
import { Box } from "@mui/material";
import { colors, tokens } from "../../../../utils";
import { autoTextColor, INK_TEXT, WHITE_TEXT } from "../../../../utils/contrast";
import { focusRing } from "../../../../theme/tokens";
import { KEBAB_MENU_TOGGLE_SIZE } from "../../../KebabContextMenu";
import { WHEEL_VISIBILITY } from "../../../../constants/wheelVisibility";
import { WHEEL_EDGE_FILL_SCALE } from "../../../BrandWheel";
import {
  COLLECTION_TILE_RADIUS,
  COLLECTION_SELECTED_RING_OFFSET,
  COLLECTION_SELECTED_RING_WIDTH,
  COLLECTION_SELECTED_RING_RADIUS,
  COLLECTION_SELECTED_RING_OUTSET,
} from "./collectionSelectedRing";

// ---- Collections: card geometry (#920 §2, circular cover #920d §1/§2) -----
// "Small card ~Create-Wheel tile size, label room for min 7 chars + '…'".
//
// gordon-0923-P1-web-gallery-collections-row-instagram-sizing-no-count:
// Gordon decision 2026-09-23 10:20 PT set the top-level Collections row's
// overall footprint (a ~64pt ring — that footprint, not the raw cover, is
// the card's width, and what every badge's corner-offset math anchors to;
// see KEBAB_BADGE_CORNER_OFFSET/LOCK_BADGE_CORNER_OFFSET below). That part
// still holds.
//
// kelly-1005-P0-collection-circles-match-prod-REPEAT (Kelly 2026-10-05,
// repeat complaint): the SAME decision also put a ~3pt GAP between the
// cover and the ring, leaving a visible white halo between the coloured
// segments and the thin grey line — Kelly: "we've got this large gap...
// whenever it's an iYuga wheel there's a pretty large space between the
// gray line", contrasted against production (web.iyuga.net/gallery/22),
// where the coloured ring sits tight against the grey line with no gap.
// Kelly explicitly wants the 0923 FOOTPRINT kept ("I like the size of
// them") with the GAP closed to match production. Measured live
// (web-dev.iyuga.net main.a0b1030e.js): ring 64px / frame 56px — a 4px gap
// per side — vs production (web.iyuga.net main.62f4b41c.js): frame 96px /
// inner content 94px, a ~1px gap (just the hairline), i.e. no gap at all.
// Fix: GAP -> 0 and COLLECTION_RING_SIZE becomes the fixed anchor (still
// 64, unchanged — every existing footprint-based consumer, badge offset,
// and the "keep this size" ask are all untouched); COLLECTION_COVER_SIZE is
// now DERIVED from it instead of the other way around, so the cover grows
// to fill the ring right up to its stroke, same proportions as production.
export const COLLECTION_RING_GAP = 0;
// Unscaled hairline, matching the existing 0.5px hairline CollectionCoverFrame
// already carries around the cover itself — this is a SEPARATE, always-
// visible grey ring drawn further OUT, not a replacement for that hairline.
export const COLLECTION_RING_STROKE_WIDTH = 1;
// The tile's overall footprint — UNCHANGED from the 0923 decision (64px).
// Kept as the fixed anchor so the card width / badge offset math / the
// Add-Collection circle (all keyed to this constant) don't move at all;
// only the inner cover (below) grows to close the gap.
export const COLLECTION_RING_SIZE = 64;
// The cover now fills the ring minus just the stroke on both sides (no
// gap) — 64 - 2*1 = 62. Derived (not hand-set) so the two can never drift
// back out of sync the way the gap-based formula silently could.
export const COLLECTION_COVER_SIZE =
  COLLECTION_RING_SIZE - COLLECTION_RING_GAP * 2 - COLLECTION_RING_STROKE_WIDTH * 2;
// The named reference (pre-existing rationale, still holds at the new size):
// the header's Create-Wheel control is a 60x60 icon button (OwnerView.jsx) —
// the card stays in that same small size class. The card's own width is now
// the ring's outer footprint (COLLECTION_RING_SIZE), not the raw cover, since
// the ring — not the cover — is what visually fills the tile edge-to-edge.
// Exported (not inlined) so the label-room guarantee is assertable rather
// than eyeballed — jsdom has no layout engine, so a rendered-width test is
// not available here.
export const COLLECTION_CARD_WIDTH = COLLECTION_RING_SIZE;
// gordon-0916: Selected-ring tokens live in collectionSelectedRing.js;
// re-exported here so existing FoldersV2 consumers import one styles module.
export {
  COLLECTION_TILE_RADIUS,
  COLLECTION_SELECTED_RING_OFFSET,
  COLLECTION_SELECTED_RING_WIDTH,
  COLLECTION_SELECTED_RING_RADIUS,
  COLLECTION_SELECTED_RING_OUTSET,
};
// The outset ring as box-shadow spreads: fill out to the offset, then the
// purple hairline. A spread shadow follows border-radius + spread, so the
// ring's inner curve is exactly COLLECTION_SELECTED_RING_RADIUS. Paints under
// the tile's content and needs no layout space (no shift on select).
// gordon-0921-WEB-gallery-theme-hover-and-selected-highlights-must-use-the-
// theme-accent-colour-sweep: the ring is a SELECTED highlight, so both bands
// now read the accent — the pale fill via --gt-hover-fill, the hairline via
// --gt-accent — falling back to today's exact purple pair when unthemed.
export const COLLECTION_SELECTED_RING_SHADOW = `0 0 0 ${COLLECTION_SELECTED_RING_OFFSET}px var(--gt-hover-fill, ${colors.PrimaryPurple100}), 0 0 0 ${COLLECTION_SELECTED_RING_OUTSET}px var(--gt-accent, ${colors.PrimaryPurple})`;
// gordon-0923-P1: 12pt regular per the IG reference (down from 12.5/600 —
// see CollectionLabel below for the weight change).
export const COLLECTION_LABEL_FONT_SIZE = 12;
// Horizontal padding CollectionLabelRow takes off the card's width.
export const COLLECTION_LABEL_ROW_PADDING_PX = 4;
// QA-1261 — the HARD upper bound on the name label's own box. The label already
// carries nowrap/ellipsis/overflow-hidden, but before this its truncation
// depended ENTIRELY on the flex-shrink chain inside the fixed-width card; a
// label with no width ceiling of its own re-grows the instant it lands in a
// min-content / inline-flex ancestor (the pre-redesign `<p>` bug: a long name
// grew the label and DISPLACED the sibling collection cards). Capping the label
// at the card's usable width makes truncation a property of the label itself,
// not of its ancestry, so a long name can NEVER push its neighbours regardless
// of the container.
//
// gordon-0923-P2-web-collections-label-truncate-like-instagram (ADDENDUM to
// gordon-0923-P1) — Gordon: "the character count displayed should also match
// Instagram's" (~7-8 visible chars, e.g. "Wall Ha…" / "Tablecl…"). The P1
// value here (COLLECTION_CARD_WIDTH - COLLECTION_LABEL_ROW_PADDING_PX = 60px)
// was sized off the TILE's footprint, not measured against the real DM Sans
// 12px/400 webfont — live-measured (Playwright + Google Fonts DM Sans,
// 2026-09-23) it let 9-11 characters show before the ellipsis, visibly looser
// than Instagram. The same measurement at the CIRCLE's own diameter
// (COLLECTION_COVER_SIZE, 56px — not the wider ring+gap footprint) gives
// [7, 8, 7, 8] visible characters across several representative names,
// matching Gordon's two examples exactly. "At the circle's width" per the
// addendum, so the label now anchors to the cover, not the tile.
export const COLLECTION_LABEL_MAX_WIDTH_PX = COLLECTION_COVER_SIZE;
// Conservative (real-webfont-measured) average advance width and ellipsis
// glyph width for DM Sans 12px/400 — used only by the label-room assertion
// below, never for layout (the browser's own text-overflow:ellipsis does the
// real, width-based, non-hard-slice clipping).
export const COLLECTION_LABEL_CHAR_PX = 6;
export const COLLECTION_LABEL_ELLIPSIS_PX = 12;
// The spec's floor: a label must always have room for at least this many
// characters before the ellipsis takes over.
export const COLLECTION_LABEL_MIN_CHARS = 7;

// #951 / gordon-0920 — spring-load: hover a Wheel drag over a Collection for
// this long before it trembles and opens in place (so a nested subfolder
// becomes a drop target). Shared with CollectionCard.jsx so the JS timer and
// the CSS tremble duration below can't drift apart. Owner spec (2026-09-20):
// dwell ~600-800ms total with a visible cue during it — DELAY (500) + TREMBLE
// (200) = 700ms, inside that window.
export const COLLECTION_SPRING_LOAD_DELAY_MS = 500;
export const COLLECTION_TREMBLE_MS = 200;

// ---- Collections: card (drag source + drop target) ------------------------
// Was a one-line row with an office-folder glyph (getFolderIcon -> PublicFolder/
// PrivateFolder/PrimaryFolder .svg). #920 §2 replaces that with an Instagram-
// style card: a circular wheel-thumbnail cover (#920d) with the name beneath
// it. The drag/drop and visibility affordances are unchanged — only the frame
// is; the actions kebab moved from an always-on top-right chip to a
// hover-revealed bottom-right control (#920d §5, CollectionCoverActions below).
// #951 — the spring-load "tremble": a small damped rock (translate + rotate,
// GPU-only) that plays once, right before the card auto-opens under a
// hovering Wheel drag. Never a scale/skew that would read as the tile itself
// being resized.
const tremble = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  20% { transform: translate(-3px, 0) rotate(-2deg); }
  40% { transform: translate(3px, 0) rotate(2deg); }
  60% { transform: translate(-2px, 0) rotate(-1deg); }
  80% { transform: translate(2px, 0) rotate(1deg); }
`;

export const FolderTileV2 = styled(Box, {
  shouldForwardProp: (prop) =>
    !["isOver", "canDrop", "isDragging", "isInvalid", "selected", "isTrembling"].includes(
      prop
    ),
})(({ isOver, canDrop, isDragging, isInvalid, selected, isTrembling }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: 6,
  width: COLLECTION_CARD_WIDTH,
  padding: 0,
  borderRadius: COLLECTION_TILE_RADIUS,
  // Drop-target feedback keeps the tile's own border; SELECTED draws the
  // outset ring below instead (gordon-0916 selected-indicator margin).
  border: `1px solid ${
    isOver && canDrop ? `var(--gt-accent, ${colors.PrimaryPurple})` : "transparent"
  }`,
  backgroundColor:
    (isOver && canDrop) || selected
      ? `var(--gt-hover-fill, ${colors.PrimaryPurple100})`
      : isOver && isInvalid
      ? alpha(colors.error, 0.08)
      : "transparent",
  cursor: isDragging ? "grabbing" : isInvalid && isOver ? "not-allowed" : "grab",
  opacity: isDragging ? 0.4 : 1,
  // QA-1265: the drag/drop feedback transitions only. The `transform 0.18s`
  // that animated the #967 hover pop-out is gone — Gordon asked the Collection
  // cards to sit FLAT (no on-hover lift, no on-hover shadow); see the removed
  // `&:hover` rule below.
  transition: "background-color 0.12s ease, border-color 0.12s ease",
  boxShadow:
    isOver && canDrop
      ? `0 0 0 2px var(--gt-hover-fill, ${colors.PrimaryPurple100}) inset`
      : selected
      ? COLLECTION_SELECTED_RING_SHADOW
      : "none",
  // #951 — plays once when the spring-load timer fires (CollectionCard),
  // right before the card auto-opens under a hovering Wheel drag.
  animation: isTrembling ? `${tremble} ${COLLECTION_TREMBLE_MS}ms ease` : "none",
  minWidth: 0,
  // #920e §5 — anchors the hover kebab badge (CollectionCoverActions), which now
  // lives OUTSIDE the overflow:hidden CollectionCoverFrame so neither the badge
  // nor its dropdown gets clipped by the round cover.
  position: "relative",
  // BUG-gallery-48946c/e61ea7: a drag source must never let the pointer-drag
  // highlight page text instead of grabbing the tile.
  userSelect: "none",
  // QA-1265 — Gordon: REMOVE the on-hover shadow / card-lift on Collection
  // cards. The #967 `&:hover` rule (transform: translateY(-3px) +
  // box-shadow: 0 8px 20px …) is deleted so the card stays flat on hover — no
  // elevation, no drop-shadow. The drag/drop border+fill feedback above is the
  // only hover-independent visual affordance and is untouched. This rule was
  // local to FolderTileV2 (Collection cards only); no other card shares it, so
  // every other card's hover is unaffected.
  "&:focus-visible": {
    outline: `2px solid var(--gt-accent, ${focusRing.color})`,
    outlineOffset: 2,
  },
  // #951 — reduced-motion still nulls the tremble `animation` + transitions.
  // (The hover transform is gone entirely now, so no hover override is needed.)
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    animation: "none",
  },
}));

// The circular cover itself (#920d §1/§2 — same shape/size as
// AddCollectionCircle below). `position: relative` anchors the kebab /
// private / visibility badge overlays; `overflow: hidden` clips the thumbnail
// (or the default logo) to the round frame.
export const CollectionCoverFrame = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$wheelArt",
})(({ $wheelArt }) => ({
  position: "relative",
  // Gordon-reported (live main.398b5163.js) — a percentage `width` here
  // resolved against FolderTileV2's CONTENT box, which its 1px border makes
  // 2px NARROWER than FolderTileV2's own declared 96px width (border-box
  // sizing). Measured live: the frame rendered 94x96, not square, so
  // `border-radius: 50%` painted an ellipse and the full-bleed cover
  // stretched non-uniformly on X vs Y, reading as off-center. A fixed px
  // width, same as `height` below, makes the frame immune to any ancestor's
  // border/padding.
  width: COLLECTION_COVER_SIZE,
  aspectRatio: "1 / 1",
  // aspect-ratio has no effect in jsdom, so keep an explicit square height too.
  height: COLLECTION_COVER_SIZE,
  borderRadius: "50%",
  overflow: "hidden",
  // Wheel art: transparent fill (segments paint the disc). Empty BrandWheel
  // covers keep NeutralBackground. Same hairline border on BOTH so D3 covers
  // match `<BrandWheel size={96} disc />` in the Collections row.
  backgroundColor: $wheelArt ? "transparent" : colors.NeutralBackground,
  border: `0.5px solid ${colors.NeutralBorder}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  // QA-1265 — no on-hover drop-shadow on the circular cover either (Gordon:
  // remove the hover shadow/elevation). A subtle border-color tint stays as the
  // only, non-elevating, hover affordance.
  transition: "border-color 0.12s ease",
  "&:hover": {
    borderColor: `var(--gt-accent, ${colors.PrimaryPurple300})`,
  },
}));

// gordon-0923-P1-web-gallery-collections-row-instagram-sizing-no-count — the
// new IG-style ring: a plain 1pt solid grey circle drawn OUTSIDE the cover
// with a gap between them (spec point 1). Deliberately a SEPARATE box from
// CollectionCoverFrame above (which keeps its own pre-existing 0.5px hairline
// flush against the cover — unrelated, unchanged) rather than resizing that
// frame itself: CollectionCoverFrame's overflow:hidden clips the full-bleed
// cover art to ITS OWN edge, so growing it out to the ring's size would pull
// the art out to fill the gap too. This ring is sized to COLLECTION_RING_SIZE
// (== COLLECTION_CARD_WIDTH, the tile's own full width) and simply centers
// the smaller CollectionCoverFrame inside it, the same way AddCollectionCircle
// (below) is sized to match it for the "Add Collection" tile.
export const CollectionCoverRing = styled(Box)({
  width: COLLECTION_RING_SIZE,
  height: COLLECTION_RING_SIZE,
  borderRadius: "50%",
  border: `${COLLECTION_RING_STROKE_WIDTH}px solid ${colors.NeutralBorder}`,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

export const CollectionCoverImage = styled("img", {
  shouldForwardProp: (prop) => prop !== "isEmpty",
})(({ isEmpty }) => ({
  // Gordon 2026-08-27 (collcover) — a real photo/wheel-thumbnail cover is now
  // FULL-BLEED: it fills the entire circle (object-fit: cover, no inset),
  // matching how every normal wheel-thumbnail card fills its frame, rather
  // than sitting margined inside the ring at COLLECTION_COVER_INNER_RATIO
  // (QA-1013's inset-inside-the-ring look is superseded by this ask). The
  // empty-state default logo (#920e §3) is unchanged: SMALL, CENTERED, shown
  // whole (contain), not cropped.
  width: isEmpty ? "48%" : "100%",
  height: isEmpty ? "48%" : "100%",
  objectFit: isEmpty ? "contain" : "cover",
  borderRadius: isEmpty ? 0 : "50%",
  position: "relative",
  zIndex: 1,
  display: "block",
  // The cover is a drag source; the browser's native image drag-ghost would
  // compete with the app's own drag layer.
  WebkitUserDrag: "none",
  userSelect: "none",
}));

// QA-1265 — hosts the shared D3 WheelComponent that renders the collection's
// SHOWCASED wheel (its real segment arcs + center portrait + frame) filling the
// round cover, replacing the flat cropped <img>. Absolute-inset so the round
// `overflow: hidden` of CollectionCoverFrame clips it to the circle exactly as
// it clipped the image.
export const CollectionCoverWheelArt = styled(Box)({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& > div": {
    width: "100%",
    height: "100%",
  },
  // Force the D3 svg to fill the round cover (createPie stamps a px width that
  // can lag 2–3px short of the frame). Stretching matches BrandWheel's full fill.
  "& svg": {
    display: "block",
    width: "100% !important",
    height: "100% !important",
  },
});

// #920e §3 — the DEFAULT WHEEL FRAME/ring around an empty collection's logo.
// Empty collections were missing the brand-wheel ring that every other default
// wheel avatar carries; this wraps the canonical BrandWheel ring so it fills the
// round cover frame, with the logo layered centered on top of it (zIndex above).
//
// gordon-0917-collection-default-cover-wedge-rim-gap — width/height:100% alone
// only stretches BrandWheel's 120x120 viewBox to the frame's own box; it does
// NOT close BrandWheel's own built-in ~6.7% margin (WHEEL_R=56 of a WHEEL_C=60
// half-width — intentional breathing room everywhere else BrandWheel is used
// as a decorative mark). That left the default cover's wedge ring measurably
// SMALLER than the real-cover D3 wheel's edge-to-edge fill (CollectionCard.jsx
// wheelArtProps margin:0), reading as a visible gap to the grey rim that the
// real cover doesn't have (MEASURED live web-dev: real-cover gap 0px vs
// default-cover gap ~3.1px on a 96px cover). Scaling the whole SVG by
// WHEEL_EDGE_FILL_SCALE (WHEEL_C / WHEEL_R) moves the ring's outer radius out
// to the viewBox edge, matching the D3 cover's zero-gap fill exactly.
export const CollectionCoverWheelFrame = styled(Box)({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 0,
  "& > svg": {
    width: "100%",
    height: "100%",
    transform: `scale(${WHEEL_EDGE_FILL_SCALE})`,
    transformOrigin: "50% 50%",
  },
});

// #920d §5 / #920e §5 — the per-collection actions (kebab) overlay. HIDDEN by
// default and mounted only on hover/focus (CollectionCard tracks that as React
// state and conditionally renders this, so "hidden" means genuinely absent from
// the DOM, not just visually covered — see CollectionCard.hoverActions test).
//
// #920e §5(a): the kebab used to sit bottom-right INSIDE CollectionCoverFrame,
// whose `overflow: hidden` (round clip) TRAPPED both the kebab and its dropdown
// — options were invisible/unclickable. It now renders as its OWN small round
// badge OUTSIDE the frame, overlapping the circle's UPPER-RIGHT corner
// (Instagram-highlight-edit style), anchored to FolderTileV2 (which has no
// overflow clip). The badge is still a DOM descendant of the tile, so
// hovering it does not fire the tile's mouseleave (mouseenter/leave follow
// the DOM tree, not geometry) and the overlay stays mounted. The dropdown
// itself escapes via a portal (isShowPortal, wired in FolderTileDnd).
//
// #920g: #920e's flat `-4`/`-4` offset put the badge's CENTER past the ring
// entirely — floating clear of the circle instead of sitting on it. The
// badge's CENTER must instead land ON the ring, at the 45° upper-right point
// (a notification badge sitting on an avatar corner). The badge is a direct
// child of FolderTileV2 (its positioning container), which is
// COLLECTION_RING_SIZE wide — gordon-0923-P1: since the IG ring now fills the
// tile edge-to-edge (COLLECTION_CARD_WIDTH === COLLECTION_RING_SIZE), not the
// smaller inner cover, this formula anchors to COLLECTION_RING_SIZE (was
// COLLECTION_COVER_SIZE, back when the two were equal at 96 == 96) so the
// badge keeps landing on the ring's own visible edge rather than the smaller
// cover's. With circle center (r, r) and radius r = COLLECTION_RING_SIZE/2,
// that point is (r + r·cos45°, r − r·sin45°). Solving
//   COLLECTION_RING_SIZE − offset − KEBAB_VISIBLE_SIZE/2 = r + r·cos45°
// (and the symmetric equation for `top`) for `offset` gives the formula
// below — see CollectionCard.kebabCorner.test.js for the geometry proof.
//
// gordon-0913-collections-kebab-circle-size-and-alignment: the kebab's
// visible white puck is shrunk to KEBAB_VISIBLE_SIZE (== the visibility icon's
// own diameter below, so the two read as a matched stacked pair) rather than
// the 32px toggle's own size — Kelly 2026-08-24 #4's "badge was 28px while the
// toggle is 32, so the toggle poked past it" (G-139) no longer applies the
// same way, because the toggle is no longer what draws the visible circle.
// The toggle (KebabContextMenu's MenuButton, KEBAB_MENU_TOGGLE_SIZE, 32,
// itself fully transparent) now sits inside a separate, larger, INVISIBLE
// KEBAB_HIT_TARGET_SIZE box so the 32px click-surface-touch-target-ruling
// hit area survives the visual shrink — see collectionCardGeometry kebab-fit
// test for both halves of that contract.
export const KEBAB_VISIBLE_SIZE = 24;
export const KEBAB_HIT_TARGET_SIZE = KEBAB_MENU_TOGGLE_SIZE;
const KEBAB_HIT_TARGET_PAD = (KEBAB_HIT_TARGET_SIZE - KEBAB_VISIBLE_SIZE) / 2;
export const KEBAB_BADGE_CORNER_OFFSET =
  (COLLECTION_RING_SIZE / 2) * (1 - Math.SQRT1_2) - KEBAB_VISIBLE_SIZE / 2;

// vy-0913-collections-hidden-badge-occluded-by-kebab-on-hover — every
// hover-revealed badge in this top-right corner (kebab, hidden-contents,
// visibility) shares ONE column and ONE row height, so a badge can never
// again be positioned independently of the others and land underneath the
// kebab's opaque puck. Row 0 is the kebab itself; row N's top offset is N
// row-heights below it, same "+4" gap CollectionVisibilityIcon already used.
export const KEBAB_STACK_ROW_HEIGHT = KEBAB_VISIBLE_SIZE + 4;
export const kebabStackRowTop = (row) =>
  KEBAB_BADGE_CORNER_OFFSET + row * KEBAB_STACK_ROW_HEIGHT;

export const CollectionCoverActions = styled(Box)({
  position: "absolute",
  // Anchored so the box's CENTER — not its edge — lands where the visible
  // puck (KEBAB_VISIBLE_SIZE) belongs on the ring; the extra
  // KEBAB_HIT_TARGET_PAD grows the box outward on every side to
  // KEBAB_HIT_TARGET_SIZE without moving that center.
  top: KEBAB_BADGE_CORNER_OFFSET - KEBAB_HIT_TARGET_PAD,
  right: KEBAB_BADGE_CORNER_OFFSET - KEBAB_HIT_TARGET_PAD,
  zIndex: 3,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: KEBAB_HIT_TARGET_SIZE,
  height: KEBAB_HIT_TARGET_SIZE,
  borderRadius: "50%",
  // The visible white puck is a decorative ::before, sized to
  // KEBAB_VISIBLE_SIZE and centered in the (larger, invisible)
  // KEBAB_HIT_TARGET_SIZE hit box — see the block comment above.
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    margin: "auto",
    width: KEBAB_VISIBLE_SIZE,
    height: KEBAB_VISIBLE_SIZE,
    borderRadius: "50%",
    backgroundColor: alpha(colors.white, 0.96),
    boxShadow: `0 1px 5px ${alpha(colors.black, 0.28)}`,
    backdropFilter: "blur(2px)",
    WebkitBackdropFilter: "blur(2px)",
  },
  // The real toggle button (`actions`, a flex child) must paint ABOVE the
  // decorative ::before puck, not be hidden beneath it.
  "& > *": {
    position: "relative",
    zIndex: 1,
  },
});

// ISP-9999 (Gordon 2026-08-29, #920vis) — owner-only visibility status
// ICON, revealed on hover directly BENEATH the kebab badge above. Same
// hover/focus-gated mount-not-CSS-hide pattern as CollectionCoverActions (see
// CollectionCard's `showActions`/`isHovered`) and anchored via the SAME right
// offset so the two stack in one visual column (top edge >= kebab bottom,
// per the #4764 placement fix this supersedes).
//
// SUPERSEDES the plain-text "Public"/"Private"/"Hidden" pill #4764 shipped:
// Gordon wants the SAME color-coded line-art icon a Wheel card already shows
// for its visibility (WheelStatus.jsx's `StatusBox`) reused here rather than
// a bespoke text chip, so a Collection and a Wheel read as ONE visibility
// vocabulary instead of two. `visibility` takes the raw wire value (1/2/3)
// directly, mirroring StatusBox's own prop shape; the background-color
// formula (HIDDEN=PrimaryPurple100, PUBLIC=LightGreenBackground,
// PRIVATE=TimedWheel) and the focus-visible ring are copied from that same
// component, not reinvented.
//
// gordon-0913-collections-kebab-circle-size-and-alignment: width/height now
// reference KEBAB_VISIBLE_SIZE (was a literal 24, which happened to match
// but wasn't provably tied to the kebab) so the "kebab circle width == badge
// width" contract holds structurally rather than by coincidence, and the top
// offset is `+ KEBAB_VISIBLE_SIZE + 4` (was `+ KEBAB_BADGE_SIZE + 4`, i.e.
// the OLD 32px kebab) so the 4px gap is measured from the kebab's new
// visible edge, not its invisible hit-target edge.
//
// `stackRow` (default 1, directly beneath the kebab) places this in the
// shared kebab-stack column at KEBAB_STACK_ROW_HEIGHT intervals.
export const CollectionVisibilityIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "stackRow",
})(({ visibility, stackRow = 1 }) => ({
  position: "absolute",
  top: kebabStackRowTop(stackRow),
  right: KEBAB_BADGE_CORNER_OFFSET,
  zIndex: 3,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: KEBAB_VISIBLE_SIZE,
  height: KEBAB_VISIBLE_SIZE,
  borderRadius: "50%",
  backgroundColor:
    visibility === WHEEL_VISIBILITY.HIDDEN
      ? colors.PrimaryPurple100
      : visibility === WHEEL_VISIBILITY.PUBLIC
      ? colors.LightGreenBackground
      : colors.TimedWheel,
  boxShadow: `0 1px 4px ${alpha(colors.black, 0.22)}`,
  transition: "opacity 0.12s ease",
  "&:focus-visible": {
    outline: `2px solid var(--gt-accent, ${colors.PrimaryPurple700})`,
    outlineOffset: "2px",
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
}));

// Kelly 2026-08-24 #5 (kelly-0824-collections-lock-icon-placement): the lock
// badge USED to render INSIDE CollectionCoverFrame (the round wheel cover), so
// it read as the WHEEL being locked. It now renders as a direct child of
// FolderTileV2 (like the kebab CollectionCoverActions), anchored to the card's
// TOP-LEFT corner — mirroring the kebab's top-RIGHT corner. Its center sits on
// the circle's upper-left 45° ring point, symmetric to KEBAB_BADGE_CORNER_OFFSET
// (see CollectionCard.lockCardCorner geometry test).
//
// gordon-0923-P1: anchors to COLLECTION_RING_SIZE, same reasoning as
// KEBAB_BADGE_CORNER_OFFSET above — the badge's positioning container
// (FolderTileV2) is COLLECTION_RING_SIZE wide, not the smaller inner cover.
export const LOCK_BADGE_SIZE = 22;
export const LOCK_BADGE_CORNER_OFFSET =
  (COLLECTION_RING_SIZE / 2) * (1 - Math.SQRT1_2) - LOCK_BADGE_SIZE / 2;

export const CollectionPrivateBadge = styled(Box)({
  position: "absolute",
  top: LOCK_BADGE_CORNER_OFFSET,
  left: LOCK_BADGE_CORNER_OFFSET,
  zIndex: 3,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: LOCK_BADGE_SIZE,
  height: LOCK_BADGE_SIZE,
  borderRadius: "50%",
  backgroundColor: alpha(colors.white, 0.96),
  boxShadow: `0 1px 5px ${alpha(colors.black, 0.28)}`,
  backdropFilter: "blur(2px)",
  WebkitBackdropFilter: "blur(2px)",
});

// #920e §4 — the name sits CENTERED under the circle. `justifyContent: center`
// centers the [name (count)] group as a unit under the round cover.
export const CollectionLabelRow = styled(Box)({
  display: "flex",
  alignItems: "baseline",
  justifyContent: "center",
  gap: 4,
  minWidth: 0,
  padding: "0 2px",
});

export const CollectionLabel = styled("span")({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: COLLECTION_LABEL_FONT_SIZE,
  // gordon-0923-P1-web-gallery-collections-row-instagram-sizing-no-count:
  // regular (400) per the IG reference, down from 600.
  fontWeight: 400,
  lineHeight: "16px",
  // vy-0913-themed-gallery-collection-label-contrast — this label sits
  // directly on the themed panel surface (Tabs.jsx sets --gt-ink there),
  // same as CollectionTab below; unset --gt-ink keeps today's hardcoded
  // purple, byte-identical.
  color: `var(--gt-ink, ${colors.PrimaryPurple900})`,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
  // QA-1261 — the label's OWN width ceiling, so a long collection name
  // truncates with the ellipsis and never displaces sibling cards even if the
  // flex context stops constraining it. See COLLECTION_LABEL_MAX_WIDTH_PX.
  maxWidth: COLLECTION_LABEL_MAX_WIDTH_PX,
  // #920e §4 — center-aligned name text. `flex: 0 1 auto` (was `flex: 1`) lets
  // the label take only the width it needs so the centered row keeps the name
  // truly centered under the circle rather than left-anchored in a full-width
  // flex child; it still shrinks + ellipsizes on overflow (minWidth: 0 above).
  textAlign: "center",
  flex: "0 1 auto",
});

// ---- Collections: add-new card (#920b) ------------------------------------
// A gray circle with a dashed/hashed outline and a plus, LOCKED to the first
// carousel position. `position: sticky` (on CarouselLockedSlot below) is what
// "locked" means in a horizontal scroller: the card stays pinned at the row's
// left edge while the collections scroll past it. Owner-only — the caller
// decides that; this is only the frame.
// #920e §1 — a collection card renders inside FolderTileOuterWrap (padding: 5)
// wrapping FolderTileV2 (1px transparent border), so its circle's top sits
// 5 + 1 = 6px below the wrapper top. The Add card had neither, so it floated
// ~6-7px HIGHER than the collection circles beside it. This top padding drops
// the Add circle onto the SAME baseline. Kept in sync with FolderTileOuterWrap
// `padding` + FolderTileV2 `borderTopWidth` by AddCollectionCard.topAlign test.
export const ADD_COLLECTION_CARD_TOP_INSET = 6;

export const AddCollectionCardRoot = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
  width: COLLECTION_CARD_WIDTH,
  flex: "0 0 auto",
  background: "none",
  border: "1px solid transparent",
  padding: 0,
  paddingTop: ADD_COLLECTION_CARD_TOP_INSET,
  cursor: "pointer",
  userSelect: "none",
  "&:focus-visible": {
    outline: `2px solid var(--gt-accent, ${focusRing.color})`,
    outlineOffset: 2,
    borderRadius: 12,
  },
});

// gordon-0923-P1-web-gallery-collections-row-instagram-sizing-no-count spec
// point 1: "the Add Collection circle uses the SAME size" — sized to
// COLLECTION_RING_SIZE (a real collection tile's overall ring footprint), not
// the smaller inner COLLECTION_COVER_SIZE, so the two read as the same
// diameter side by side. Its own dashed-outline style is unchanged (spec says
// nothing about restyling it, only resizing it).
export const AddCollectionCircle = styled(Box)({
  width: COLLECTION_RING_SIZE,
  height: COLLECTION_RING_SIZE,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: colors.NeutralBackground,
  border: `2px dashed ${colors.StrokePrimary}`,
  color: colors.darkGrey,
  transition: "background-color 0.12s ease, border-color 0.12s ease",
  "&:hover": {
    borderColor: `var(--gt-accent, ${colors.PrimaryPurple})`,
    backgroundColor: `var(--gt-hover-fill, ${colors.PrimaryPurple100})`,
    color: `var(--gt-accent-ink, ${colors.PrimaryPurple700})`,
  },
});

// gordon-0917-collections-empty-state-own-gallery-web-rn — the wide
// dashed-border card shown INSTEAD of the carousel when the owner's gallery
// has zero collections (plus circle on the left, title + body to its right).
// Deliberately its own root (not a reuse of AddCollectionCardRoot, which is a
// narrow ~96px carousel tile) since this card spans the row's width.
export const CollectionsEmptyCardRoot = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  width: "100%",
  padding: "16px",
  borderRadius: 12,
  border: `1px dashed ${colors.StrokePrimary}`,
  background: "none",
  cursor: "pointer",
  textAlign: "left",
  transition: "background-color 0.12s ease, border-color 0.12s ease",
  "&:hover": {
    borderColor: `var(--gt-accent, ${colors.PrimaryPurple})`,
    backgroundColor: `var(--gt-hover-fill, ${colors.PrimaryPurple100})`,
  },
  "&:focus-visible": {
    outline: `2px solid var(--gt-accent, ${focusRing.color})`,
    outlineOffset: 2,
  },
});

export const CollectionsEmptyCardCircle = styled(Box)({
  flex: "0 0 auto",
  width: 40,
  height: 40,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `2px dashed ${colors.StrokePrimary}`,
  color: colors.darkGrey,
});

export const CollectionsEmptyCardTextColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
});

// ---- Collections: carousel container (#920 §3, grid removed by #962) ------
export const CollectionsCarouselRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  // Positioned so the purple drag-reorder insertion line (rendered as a DOM
  // child of this scroller, see CollectionsSurface) resolves its left/top
  // against THIS box, not an ancestor — it must scroll with the row's own
  // content, exactly like ArrangeableGrid's insertion line does inside its grid.
  position: "relative",
  overflowX: "auto",
  overflowY: "hidden",
  // gordon-0916: room for a selected tile's outset ring on the top/left/right
  // edges (bottom already has 8px >= the outset), pulled back with an equal
  // negative margin so the cards themselves do not move.
  paddingTop: COLLECTION_SELECTED_RING_OUTSET,
  marginTop: -COLLECTION_SELECTED_RING_OUTSET,
  paddingLeft: COLLECTION_SELECTED_RING_OUTSET,
  marginLeft: -COLLECTION_SELECTED_RING_OUTSET,
  paddingRight: COLLECTION_SELECTED_RING_OUTSET,
  marginRight: -COLLECTION_SELECTED_RING_OUTSET,
  paddingBottom: 8,
  scrollSnapType: "x proximity",
  scrollPaddingLeft: COLLECTION_CARD_WIDTH + 10,
  WebkitOverflowScrolling: "touch",
  // stgfix1008-NEW-Gallery-1-collections-hscrollbar-1440 (repeat class,
  // Kelly/Terry/Gordon 2026-10-08): the themed thumb this row used to paint
  // (gordon-0918c-accent-on-small-surfaces) was a permanently-visible
  // ::-webkit-scrollbar, not an OS overlay one, so a populated owner gallery
  // (MEASURED web-stg.iyuga.net/gallery/55371 @1440: scrollWidth 1804 vs
  // clientWidth 1050) always showed a flush horizontal bar under the row.
  // Hidden the same way TabStrip already hides its own native scrollbar
  // (below) — overflowX stays "auto" so scrolling itself (wheel/trackpad/
  // drag) and the explicit left/right arrow buttons (CollectionsSurface's
  // scrollBy, unaffected by scrollbar chrome) keep working.
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
});

// Wraps the add-new card so it stays pinned at the left edge of the scroller.
// vy-0913-themed-gallery-add-collection-tile-white-block: this backdrop must
// stay OPAQUE (it hides scrolled-past cards sliding under the sticky slot),
// but a hardcoded `colors.white` painted a hard white rectangle over a themed
// dark panel — every sibling FolderTileV2 tile is `transparent` and lets the
// panel show through, so only this locked slot broke from the pack. Reads the
// SAME `--gt-surface` custom property the panel itself sets (Tabs.jsx,
// pages/Gallery/index.jsx), so the slot's backdrop always matches the panel
// behind it; unset (default theme) falls back to today's plain white,
// byte-identical.
export const CarouselLockedSlot = styled(Box)({
  position: "sticky",
  // Sticky insets resolve against the scroller's padding edge; the row now
  // carries COLLECTION_SELECTED_RING_OUTSET of left padding, so pin the slot
  // that far back out to keep it flush with the visible left edge.
  left: -COLLECTION_SELECTED_RING_OUTSET,
  zIndex: 2,
  flex: "0 0 auto",
  paddingRight: 6,
  backgroundColor: "var(--gt-surface, " + colors.white + ")",
});

// ---- Collections: reorder edge (nested drop target) ------------------------
// Thin padding wrapping each card. react-dnd calls the innermost drop target
// first, so a drop ON the card (FolderTileV2) is claimed by the nest target and
// never reaches here; this only lights up/fires when the drop lands in the
// padding beside the card — i.e. "drop beside" = reorder, "drop on" = nest
// (bug #408/#403, Gordon's nested-folders decision).
export const FolderTileOuterWrap = styled(Box, {
  shouldForwardProp: (prop) => !["isOver", "canDrop"].includes(prop),
})(({ isOver, canDrop }) => ({
  flex: "0 0 auto",
  scrollSnapAlign: "start",
  padding: 5,
  borderRadius: 14,
  border: `1px dashed ${
    isOver && canDrop ? `var(--gt-accent, ${colors.PrimaryPurple})` : "transparent"
  }`,
  backgroundColor: isOver && canDrop ? `var(--gt-hover-fill, ${alpha(colors.PrimaryPurple, 0.06)})` : "transparent",
  transition: "background-color 0.12s ease, border-color 0.12s ease",
}));

// ---- Owner: wheel wrapper (drag source + reorder drop target) -------------
// QA-129 + BUG-gallery-48946c/e61ea7: user-select:none is load-bearing, not
// cosmetic. Without it the mouse-down that begins a drag first starts a NATIVE
// TEXT SELECTION on the card, which sweeps across the page headings /
// "Folders (n)" / empty-state copy instead of grabbing the card (exactly what
// Kelly's frames showed). Making the whole draggable subtree unselectable forces
// the browser into drag mode. Exported as a pure object so the no-text-selection
// guarantee can be asserted without depending on jsdom applying emotion's
// injected CSS.
export const wheelDragWrapperStyles = (isDragging) => ({
  cursor: "grab",
  opacity: isDragging ? 0.4 : 1,
  transition: "opacity 0.12s ease, box-shadow 0.12s ease",
  height: "100%",
  borderRadius: 12,
  userSelect: "none",
  WebkitUserSelect: "none",
  MozUserSelect: "none",
  msUserSelect: "none",
  WebkitTouchCallout: "none",
  // suppress the browser's default image/element drag-ghost on children so the
  // only ghost is our clean single-card drag layer (QA-129 WheelDragLayer).
  WebkitUserDrag: "none",
  "&:active": { cursor: "grabbing" },
});

// `isOver`/`canReorder` render the insertion affordance when another wheel is
// dragged over this one (QA-129 reorder drop target).
export const WheelDragWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    !["isDragging", "isOver", "canReorder"].includes(prop),
})(({ isDragging, isOver, canReorder }) => ({
  ...wheelDragWrapperStyles(isDragging),
  boxShadow:
    isOver && canReorder ? `0 0 0 2px var(--gt-accent, ${colors.PrimaryPurple}) inset` : "none",
}));

// ---- Owner: breadcrumb / root drop zone -----------------------------------
export const DropZonePill = styled(Box, {
  shouldForwardProp: (prop) => !["isOver", "canDrop"].includes(prop),
})(({ isOver, canDrop }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px",
  borderRadius: 16,
  cursor: "pointer",
  border: `1px dashed ${
    isOver && canDrop ? `var(--gt-accent, ${colors.PrimaryPurple})` : "transparent"
  }`,
  backgroundColor: isOver && canDrop ? `var(--gt-hover-fill, ${colors.PrimaryPurple100})` : "transparent",
  transition: "background-color 0.12s ease, border-color 0.12s ease",
  userSelect: "none",
}));

// ---- Guest: collection filter chips ---------------------------------------
export const ChipRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  overflowX: "auto",
  overflowY: "hidden",
  paddingBottom: 6,
  scrollbarWidth: "thin",
  WebkitOverflowScrolling: "touch",
  "&::-webkit-scrollbar": { height: 6 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: colors.NeutralBorder,
    borderRadius: 3,
  },
});

export const FilterChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected",
})(({ selected }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  flex: "0 0 auto",
  maxWidth: 220,
  padding: "6px 14px",
  borderRadius: 18,
  fontFamily: "DM Sans",
  fontSize: 14,
  lineHeight: "20px",
  fontWeight: selected ? 600 : 500,
  cursor: "pointer",
  whiteSpace: "nowrap",
  // gordon-0921-WEB-gallery-theme-hover-and-selected-highlights-must-use-the-
  // theme-accent-colour-sweep: selected = a SOLID accent fill, so its label
  // reads --gt-accent-on-fill-text (WCAG AA 4.5:1 against the accent itself,
  // via the shared D-11 autoTextColor helper — never a hand-picked hex);
  // unselected reads --gt-accent-ink-text (text on the pale fill below).
  color: selected
    ? `var(--gt-accent-on-fill-text, ${colors.white})`
    : `var(--gt-accent-ink-text, ${colors.PrimaryPurple900})`,
  // DL-004 reach (ISP-4018): selected = on-fill-text token over a SOLID
  // accent fill. Border stays the exact accent (outline, not a fill).
  backgroundColor: selected
    ? `var(--gt-accent, ${tokens.brand.primaryActive})`
    : `var(--gt-hover-fill, ${colors.PrimaryPurple100})`,
  border: `1px solid ${
    selected
      ? `var(--gt-accent, ${colors.PrimaryPurple})`
      : `var(--gt-hover-fill, ${colors.PrimaryPurple100})`
  }`,
  transition: "background-color 0.12s ease, color 0.12s ease",
  "&:hover": {
    backgroundColor: selected
      ? `var(--gt-accent, ${tokens.brand.primaryActiveHover})`
      : `var(--gt-hover-fill, ${colors.PrimaryPurple300})`,
  },
  "&:focus-visible": {
    outline: `2px solid var(--gt-accent, ${focusRing.color})`,
    outlineOffset: 2,
  },
}));

export const ChipLabel = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

// ---- Owner: "Wheel collections" tab strip ---------------------------------
// Matches the approved deck's .nx-trow / .nx-tabs / .nx-tab rules
// (design-handoff/concepts/gallery-folders-elevated/01-owner-with-businessbox.html):
// a flush row with a 1px bottom rule, 22px gaps, DM Sans 14.5/600, inactive
// #565556 (colors.darkGrey), active #8126DB (PrimaryPurple700) with a 3px
// #9B5AF8 (PrimaryPurple) underline sitting ON the rule.
export const TabStripRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  // stgfix-10101745-T117 (MEASURED live web-stg main.a1a75c38.js @390x844,
  // owner gallery, with AND without search): with the row on the default
  // flexWrap:nowrap, the trailing slot (Sort + Personal/Team switcher,
  // OwnerView.tabsTrailingSlot) kept its full width and starved the tab
  // strip (TabStripScrollWrap, minWidth:0) down to 113px — only "Created"
  // was visible — and squeezed "Sort: My order" to 57px, 3 lines tall.
  // Wrapping lets the tab strip keep the full row (its flex-basis is the
  // tabs' own width, so it breaks onto its own line only when the trailing
  // controls don't fit beside it) and drops the controls to a second line.
  // A no-op whenever everything fits (1440: same single 45px row).
  flexWrap: "wrap",
  columnGap: 14,
  rowGap: 4,
  borderBottom: `1px solid ${colors.NeutralBorder}`,
  marginTop: 2,
  marginBottom: 14,
});

export const TabStrip = styled(Box)({
  display: "flex",
  alignItems: "flex-end",
  gap: 22,
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
  // ISP-9999 (gordon-0916-themes-preview-search-bar-square-corners-and-
  // created-pill-clipped, bug 2) — CollectionTab's own hover/selected pill
  // (::before below) is inset left:-6/right:-6 past that button's border
  // box, by design (room to breathe beyond the 2px built-in padding). With
  // no padding here, the FIRST tab's border box starts flush at this
  // scroll container's own left edge (x:0), so that tab's pill overhangs
  // into NEGATIVE scroll-container coordinates — overflowX:"auto" clips
  // anything outside its padding box, cutting the pill's left rounded end
  // clean off. 6px padding on both edges gives every tab's pill the same
  // room the ones in the middle already have from the 22px gap.
  paddingLeft: 6,
  paddingRight: 6,
});

// collab-gallery390-tabstrip-hscroll-no-affordance: TabStrip hides its
// scrollbar (above) for the deck's flush look, but at 390px the four tabs
// (Created/Cloned/Unlocked/Favorited, each carrying an icon + count badge)
// overflow the strip's width with NOTHING telling a sighted user more tabs
// sit offscroll — "Unlocked"/"Favorited" are simply clipped mid-word.
// MEASURED live on web-stg.iyuga.net/gallery at 390px: tablist scrollWidth
// 524 vs clientWidth 342 (overflows), scrollbarWidth "none", no mask-image,
// no box-shadow, no fade element. Same edge-fade affordance convention
// RailScroller/Teams' ScrollX already established elsewhere in the app
// (soft gradient shown only on the side that actually has hidden content,
// live-tracked via scroll position) — not a new hand-rolled version of it.
export const TabStripScrollWrap = styled(Box)({
  position: "relative",
  minWidth: 0,
  flex: "1 1 auto",
});

const TAB_STRIP_FADE_WIDTH = 24;

export const TabStripFade = styled(Box, {
  shouldForwardProp: (prop) => prop !== "side" && prop !== "visible",
})(({ side, visible }) => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  [side === "left" ? "left" : "right"]: 0,
  width: TAB_STRIP_FADE_WIDTH,
  pointerEvents: "none",
  zIndex: 1,
  opacity: visible ? 1 : 0,
  transition: "opacity 0.15s ease",
  // gordon-0913-gallery-theme-panel-background-and-accent-card-border /
  // vy-0913-themed-gallery-tab-strip-fade-white-390: this fade sits directly on
  // the wheels-tabs-wrapper panel (Tabs.jsx, `var(--gt-surface, ${colors.white})`),
  // so it must fade FROM that same resolved surface colour, not a hardcoded
  // white — otherwise a dark Gallery Theme panel gets a literal white bar
  // painted over the "Created"/"Cloned" tab labels. Unset --gt-surface (default
  // theme) keeps today's white fade, byte-identical.
  background:
    side === "left"
      ? `linear-gradient(90deg, var(--gt-surface, ${colors.white}) 0%, transparent 100%)`
      : `linear-gradient(270deg, var(--gt-surface, ${colors.white}) 0%, transparent 100%)`,
}));

export const CollectionTab = styled("button", {
  shouldForwardProp: (prop) => prop !== "selected",
})(({ selected }) => ({
  background: "none",
  border: "none",
  // Deck padding is 8/2/11; the vertical padding is widened so the hit area
  // clears the 32px touch-target ruling without moving the text baseline.
  padding: "11px 2px 11px",
  minHeight: 40,
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 14.5,
  fontWeight: 600,
  lineHeight: "18px",
  // gordon-0913-gallery-theme-panel-background-and-accent-card-border (a):
  // the label/icon sits directly on the themed panel surface (Tabs.jsx) with
  // no highlight of its own, so it follows the panel's general auto-contrast
  // ink; unset --gt-ink keeps today's hardcoded grey (unselected) / purple
  // (selected), byte-identical.
  //
  // gordon-0923 owner correction (verbatim, screenshot of a themed gallery:
  // "Created text should not take on Accent Color (true for all Gallery
  // Wheel Grid headers)"): REVERSES the gordon-0921 change that had read the
  // selected label from --gt-tab-accent (a HIGHLIGHT var) — label text is
  // never a highlight, in any state (selected/unselected/hover). Both
  // branches now read the SAME --gt-ink the icon (CollectionTabs.jsx) already
  // used, matching the unselected tab's own token; only the fallback literal
  // differs per state so an unthemed gallery (--gt-ink unset) stays
  // byte-identical to before. The underline (::after) and pill fill
  // (::before) below stay on --gt-tab-accent — those ARE the highlight.
  color: selected
    ? `var(--gt-ink, ${colors.PrimaryPurple700})`
    : `var(--gt-ink, ${colors.darkGrey})`,
  cursor: "pointer",
  // #923: icon + label + count badge sit in a row (production parity —
  // WheelsTab.jsx's CustomTabs pairs every tab with an icon and a count).
  display: "flex",
  alignItems: "center",
  gap: 6,
  position: "relative",
  // z-index:0 (not "auto") makes this button its OWN stacking context, so
  // the ::before's z-index:-1 below stays scoped behind THIS button's text
  // only — without it, a negative z-index on a plain position:relative
  // descendant escapes to the nearest ANCESTOR stacking context (here, all
  // the way to the page root) and paints behind the page background instead.
  zIndex: 0,
  whiteSpace: "nowrap",
  flex: "0 0 auto",
  transition: "color 0.12s ease",
  // Hover affordance (parity with the legacy tab strip's #EFE9FE pill fill,
  // src/components/Tabs/index.jsx): a ::before behind the label text so it
  // never touches the approved font size/weight, 22px gaps or 3px underline.
  // Inset beyond the button's own 2px horizontal padding for breathing room,
  // and short of the row's bottom rule so the pill doesn't collide with it.
  // z-index:-1 keeps this behind the label text — a positioned pseudo-element
  // otherwise paints AFTER in-flow inline text and would hide it.
  //
  // gordon-0921 (owner design rule): the SELECTED tab's own pale fill (shown
  // once selected, not only on :hover below — a click naturally leaves the
  // pointer sitting on the tab, which is why the owner's screenshot showed
  // fill+text together) is the SAME pale accent tint the hover rule uses —
  // --gt-tab-hover-fill (resolveSmallSurfaceAccents' tabHoverFill, a ~12%
  // alpha tint of the raw accent). Unset keeps today's transparent default.
  "&::before": {
    content: '""',
    position: "absolute",
    top: 6,
    bottom: 6,
    left: -6,
    right: -6,
    borderRadius: 8,
    backgroundColor: selected
      ? `var(--gt-tab-hover-fill, ${colors.PrimaryPurple100})`
      : "transparent",
    transition: "background-color 0.12s ease",
    zIndex: -1,
  },
  // gordon-0923: hover no longer repaints the label from the accent (label
  // text is never a highlight) — only the pale pill fill responds to hover,
  // the text stays whatever `color` above already resolved to.
  "&:hover": {
    "&::before": {
      backgroundColor: `var(--gt-tab-hover-fill, ${colors.PrimaryPurple100})`,
    },
  },
  "&:focus-visible": {
    outline: `2px solid ${focusRing.color}`,
    outlineOffset: 2,
    borderRadius: 4,
  },
  // gordon-0918c-accent-on-small-surfaces-legible-family-color-toast: the
  // active tab's underline follows the gallery accent
  // (--gt-tab-accent, resolved against this panel's own themed surface via
  // the shared legibleAccent helper). Unset keeps today's hardcoded
  // colors.PrimaryPurple, byte-identical.
  "&::after": selected
    ? {
        content: '""',
        position: "absolute",
        left: 0,
        right: 0,
        bottom: -1,
        height: 3,
        borderRadius: "3px 3px 0 0",
        backgroundColor: `var(--gt-tab-accent, ${colors.PrimaryPurple})`,
      }
    : undefined,
}));

// #923: the count badge pill sitting after each tab's label — production's
// own #EFE9FE (selected) / #ECECEC (unselected) pill
// (src/components/Tabs/index.jsx), reusing colors.PrimaryPurple100 for the
// selected value since it is that exact hex already.
//
// gordon-0921-WEB-gallery-tab-count-pill-illegible-on-the-selected-tab-in-a-
// themed-gallery-white-on-white: MEASURED (STEP 0 positive control) — the
// OLD `color: selected ? var(--gt-tab-accent, ...) : colors.darkGrey` painted
// the SELECTED count's number from --gt-tab-accent, a value resolved for
// legibility against the PANEL surface (resolveSmallSurfaceAccents), not
// against this badge's own OPAQUE pill fill below. On a themed gallery whose
// active-tab accent legitimately resolves to (near-)white (legible against a
// dark panel), that same white then painted straight onto this pill's pale
// #EFE9FE fill — 1.18:1, WCAG AA needs 4.5:1 — reading as a blank white
// pill. Unlike CollectionTab's label/underline above (which correctly sit
// ON the panel and should track its accent), this badge has its own opaque
// background per state, so its text must be resolved against THAT
// background specifically — the shared D-11 auto-contrast helper
// (utils/contrast.js autoTextColor, the same one the rest of the app uses
// for text-on-a-customizable-surface), never the panel's accent and never a
// hand-picked hex. The pill's own fill is unchanged and never varies by
// theme, so this is deliberately theme-invariant — the fix decouples the
// number from the surrounding themed affordance entirely.
export const COLLECTION_TAB_COUNT_BG = {
  selected: colors.PrimaryPurple100,
  unselected: "#ECECEC",
};

export const collectionTabCountTextColor = (bg) =>
  autoTextColor(bg, { dark: INK_TEXT, light: WHITE_TEXT, fallback: colors.darkGrey });

export const CollectionTabCount = styled("span", {
  shouldForwardProp: (prop) => prop !== "selected",
})(({ selected }) => {
  const backgroundColor = selected
    ? COLLECTION_TAB_COUNT_BG.selected
    : COLLECTION_TAB_COUNT_BG.unselected;
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 6px",
    borderRadius: 9,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 11,
    fontWeight: 600,
    lineHeight: "16px",
    color: collectionTabCountTextColor(backgroundColor),
    backgroundColor,
  };
});

// ---- Section headings ------------------------------------------------------
export const SectionRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: 8,
  marginBottom: 8,
  // BUG-gallery-48946c/e61ea7: the "Folders (n)" header sits directly above the
  // drag grid — a drag that overshoots must not highlight the heading text.
  userSelect: "none",
  // BUG #922: anchors the absolutely-positioned .create-wheel-center-group
  // (see OwnerView.jsx) so its "left: 50%" resolves against THIS row's own
  // width, not an ancestor's.
  position: "relative",
});
